import type { ReactNode } from 'react';

import { cn } from '@/lib/cn';
import { texts } from '@/lib/texts';

import { Button } from './Button';

export function Spinner({ size = 'md', className }: { size?: 'sm' | 'md'; className?: string }) {
  return (
    <span
      role="status"
      aria-label={texts.common.loading}
      className={cn(
        'inline-block animate-spin rounded-full border-2 border-current border-t-transparent',
        size === 'sm' ? 'h-4 w-4' : 'h-7 w-7',
        className,
      )}
    />
  );
}

/** Заглушка контента на время загрузки. */
export function Skeleton({ className }: { className?: string }) {
  return (
    <div className={cn('bg-surface-2 animate-pulse rounded-lg', className)} aria-hidden="true" />
  );
}

export interface EmptyStateProps {
  title: string;
  body?: string;
  actionLabel?: string;
  onAction?: () => void;
  icon?: ReactNode;
}

/** Пустое состояние экрана: что это и что сделать. */
export function EmptyState({ title, body, actionLabel, onAction, icon }: EmptyStateProps) {
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 py-16 text-center">
      {icon ? <div className="text-muted">{icon}</div> : null}
      <h2 className="text-lg font-bold tracking-tight">{title}</h2>
      {body ? <p className="text-muted max-w-xs text-sm">{body}</p> : null}
      {actionLabel && onAction ? (
        <Button variant="secondary" onClick={onAction} className="mt-2">
          {actionLabel}
        </Button>
      ) : null}
    </div>
  );
}

export interface ErrorStateProps {
  title?: string;
  body?: string;
  onRetry?: () => void;
  retryLabel?: string;
}

/** Ошибка загрузки: коротко и кнопка «Повторить». */
export function ErrorState({
  title = texts.common.errorTitle,
  body = texts.common.errorBody,
  onRetry,
  retryLabel = texts.common.retry,
}: ErrorStateProps) {
  return (
    <div
      className="flex flex-1 flex-col items-center justify-center gap-3 px-6 py-16 text-center"
      role="alert"
    >
      <h2 className="text-lg font-bold tracking-tight">{title}</h2>
      <p className="text-muted max-w-xs text-sm">{body}</p>
      {onRetry ? (
        <Button variant="secondary" onClick={onRetry} className="mt-2">
          {retryLabel}
        </Button>
      ) : null}
    </div>
  );
}
